/* eslint-disable react/no-array-index-key */
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideYear: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        slidTextAlignment: "left" | "center" | "right";
        openLinkInNewTab: boolean;
        customCSS: string;
    };
}

const enum fields {
    timeLineBackground = "timeLineBackground",
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.timeLineBackground]: string;
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    imageContainer?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    timeline?: InjectableCss;
    dot?: TypographyPresentationProps;
    dotContainerBtn?: ButtonPresentationProps;
    nextArrowButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0px 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 64%;
            margin-top: -40px;
            left: 4%;
        `,
    },
    prevArrowButton: {
        css: css`
            background: #fff;
            border: 0;
            padding: 0;
            width: 40px;
            border-radius: 50%;
            > span {
                top: 0 !important;
                display: flex;
                justify-content: center;
                width: 100%;
            }
            &:hover {
                background: #fff;
            }
            &:focus {
                outline: none;
            }
            & svg {
                stroke: #79242f;
                height: 25px;
                width: 25px;
            }
            &:disabled {
                opacity: 0.5;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    dotContainerBtn: {
        css: css`
            text-align: center;
            background-color: transparent !important;
            display: block;
            border: 0 !important;
            color: #959595;
            outline: 0 !important;
            position: relative;
            z-index: 100;
            margin: 0 23px;
            text-align: center;
            font-size: 14px;
            &:hover {
                border: 0 !important;
                background-color: transparent !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0 3px;
                            font-size: 8px;
                        `,
                        css`
                            margin: 0 3px;
                            font-size: 8px;
                        `,
                        css`
                            margin: 0 13px;
                            font-size: 14px;
                        `,
                        css`
                            margin: 0 23px;
                            font-size: 14px;
                        `,
                        css`
                            margin: 0 23px;
                            font-size: 14px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dot: {
        css: css`
            display: block;
            width: 14px;
            height: 14px;
            background-color: #011e41;
            border-radius: 50%;
            text-indent: -9999px;
            margin: 10px auto 0;
            &:hover {
                color: #fff;
            }
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            margin-top: 36px;
            padding: 0px 17.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 23px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 23px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                        css`
                            margin-top: 36px;
                            background-color: #fff;
                            border: none;
                            padding: 0px 17.5px;
                            &:hover {
                                background-color: #fff;
                            }
                            span {
                                font-size: 12px;
                                font-weight: 600;
                                letter-spacing: 2px;
                                line-height: 20px;
                                text-align: center;
                                color: #33333a;
                                padding: 10px 18.5px;
                                text-transform: uppercase;
                                &:hover {
                                    color: #006bc0;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 22px;
            margin-bottom: 18px;
            position: absolute;
            width: 100%;
            z-index: 99;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 23px;
                        `,
                        css`
                            margin-top: 23px;
                        `,
                        css`
                            margin-top: 28px;
                        `,
                        css`
                            margin-top: 28px;
                        `,
                        css`
                            margin-top: 28px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dotButton: {
        css: css`
            background-color: #cccccc;
            padding: 0;
            margin: 0 5px;
            border: 0;
            //background: ${getColor("common.border")};
            &:hover {
                background-color: rgb(36, 36, 41);
            }
            &:active {
                background: #006bc0;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 64%;
            margin-top: -40px;
            right: 4%;
        `,
    },
    nextArrowButton: {
        css: css`
            background: #fff;
            border: 0;
            padding: 0;
            width: 40px;
            border-radius: 50%;
            > span {
                display: flex;
                justify-content: center;
                top: 0 !important;
                width: 100%;
            }
            &:hover {
                background: #fff;
            }
            &:focus {
                outline: none;
            }
            & svg {
                stroke: #79242f;
                height: 25px;
                width: 25px;
            }
            &:disabled {
                opacity: 0.5;
            }
        `,
    },
    imageContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 35%;
                        `,
                        css`
                            width: 35%;
                        `,
                    ],
                    "min",
                )}
            & img {
                max-width: 100%;
            }
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 100%;
                            text-align: center;
                        `,
                        css`
                            width: 62%;
                            text-align: left;
                        `,
                        css`
                            width: 62%;
                            text-align: left;
                        `,
                    ],
                    "min",
                )}
            display: flex;
            flex-direction: column;
            justify-content: center;
        `,
    },
    timeline: {
        css: css`
            position: absolute;
            width: 90%;
            bottom: 6px;
            border: 2px solid #011e41;
            background-color: #011e41;
            z-index: 99;
            &:before {
                content: "";
            }
            &:after {
                content: "";
            }
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const TimeLineCarousal: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const history = useHistory();
    /* const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink); */
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
            setCanScroll();
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: false,
        });
        setCanScroll();
    }, [embla, fields.slides]);

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 65vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...styles.customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                {fields.slideIndicator && (
                    <StyledWrapper {...styles.dotsContainerWrapper} className="slide-indicator">
                        {fields.slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? `            
                                    color:#fff;                        
                                    &:hover > span { background: #fff; }
                                    &:active > span {
                                        background: #fff ;
                                    }
                                    & span {
                                        background:#fff;
                                    }
                                   
                                `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${styles.dotContainerBtn}
                                    ${dotColorStyles}
                                `,
                            };
                            // eslint-disable-next-line react/no-array-index-key

                            return (
                                <Button
                                    key={i}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    className="slide-dot-button"
                                    // {...styles.dotContainerBtn}
                                >
                                    {_.fields.slideYear}
                                    <Typography {...styles.dot}>{i}</Typography>
                                </Button>
                            );
                        })}
                        <StyledWrapper {...styles.timeline}></StyledWrapper>
                    </StyledWrapper>
                )}
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                        {fields.slides.map((slide, index) => {
                            // const buttonLink = buttonLinks[index];

                            let responsiveImageBehaviorStyles;
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                responsiveImageBehaviorStyles = "background-size: cover;";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                responsiveImageBehaviorStyles = "background-size: contain;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "background-size: auto 100%;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "background-size: 100% auto;";
                            }

                            const backgroundStyles = `background-color: ${fields.timeLineBackground};`;
                            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            const containerStyles = {
                                css: css`
                                    width: 100%;
                                `,
                            };

                            const slideWrapperStyles = {
                                css: css`
                                    width: 100%;
                                    padding-top: 120px;
                                    ${styles.slideContentWrapper?.css || ""}
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    min-height: 388px;
                                                    padding-top: 50px;
                                                `,
                                                css`
                                                    min-height: 388px;
                                                    padding-top: 50px;
                                                `,
                                                css`
                                                    min-height: 530px;
                                                    padding-top: 50px;
                                                `,
                                                css`
                                                    min-height: 530px;
                                                    padding-top: 120px;
                                                `,
                                                css`
                                                    ${heightStyles}
                                                    padding-top: 120px;
                                                `,
                                            ],
                                            "min",
                                        )}
                                        ${textAlignStyles};
                                    ${responsiveImageBehaviorStyles};
                                    ${focalPointStyles};
                                    ${overlayPositioningStyles};
                                    ${fontSizeStyles};
                                    ${backgroundStyles};
                                    & > div {
                                        background-color: transparent;
                                    }
                                `,
                            };
                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                    padding: ${slide.fields.contentPadding}px;
                                    display: flex;
                                    justify-content: ${slide.fields.slidTextAlignment};
                                    ${styles.backgroundStyles}
                                    ${slide.fields.centerTextVertically &&
                                    `
                                    align-items: center;
                                    `}
                                    max-width: 1140px;
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    max-width: 100% !important;
                                                    background-color: rgba(37, 37, 37, 0.7);
                                                    justify-content: flex-start;
                                                `,
                                                css`
                                                    max-width: 100% !important;
                                                    background-color: rgba(37, 37, 37, 0.7);
                                                    justify-content: flex-start;
                                                `,
                                                css`
                                                    max-width: 720px !important;
                                                    background-color: transparent;
                                                    justify-content: flex-start;
                                                `,
                                                css`
                                                    max-width: 960px !important;
                                                    background-color: transparent;
                                                    justify-content: ${slide.fields.slidTextAlignment};
                                                `,
                                                css`
                                                    max-width: 1140px !important;
                                                    background-color: transparent;
                                                    justify-content: ${slide.fields.slidTextAlignment};
                                                `,
                                            ],
                                            "min",
                                        )}
                                    margin: 0 auto;
                                `,
                            };
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={index}>
                                    <StyledWrapper className="slide-wrapper" {...slideWrapperStyles}>
                                        <StyledWrapper {...slideOverlayWrapperStyles}>
                                            <StyledWrapper
                                                css={css`
                                                    width: 100%;
                                                    display: flex;
                                                    justify-content: space-between;
                                                    ${({ theme }: { theme: BaseTheme }) =>
                                                        breakpointMediaQueries(
                                                            theme,
                                                            [
                                                                css`
                                                                    width: 100%;
                                                                    padding: 30px;
                                                                    flex-direction: column;
                                                                `,
                                                                css`
                                                                    width: 100%;
                                                                    padding: 30px;
                                                                    flex-direction: column;
                                                                `,
                                                                css`
                                                                    width: 100%;
                                                                    padding: 22px;
                                                                    flex-direction: column;
                                                                `,
                                                                css`
                                                                    width: 100%;
                                                                    padding: 0px;
                                                                    flex-direction: row;
                                                                `,
                                                                css`
                                                                    width: 100%;
                                                                    padding: 0px;
                                                                    flex-direction: row;
                                                                `,
                                                            ],
                                                            "min",
                                                        )};
                                                `}
                                            >
                                                {slide.fields.background === "image" ? (
                                                    <StyledWrapper {...styles.imageContainer}>
                                                        <Img src={slide.fields.image} altText="test" />
                                                    </StyledWrapper>
                                                ) : null}
                                                <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                                    {slide.fields.heading && (
                                                        <Typography
                                                            {...styles.headingText}
                                                            className="slide-heading"
                                                            css={css`
                                                                p {
                                                                    font-size: 18px !important;
                                                                    font-weight: bold !important;
                                                                    letter-spacing: 1.08px !important;
                                                                    line-height: 25px !important;
                                                                    margin-bottom: 15px !important;
                                                                    ${({ theme }: { theme: BaseTheme }) =>
                                                                        breakpointMediaQueries(
                                                                            theme,
                                                                            [
                                                                                css`
                                                                                    font-size: 14px !important;
                                                                                    font-weight: bold !important;
                                                                                    letter-spacing: 0.84px !important;
                                                                                    line-height: 17px !important;
                                                                                `,
                                                                                css`
                                                                                    font-size: 14px !important;
                                                                                    font-weight: bold !important;
                                                                                    letter-spacing: 0.84px !important;
                                                                                    line-height: 17px !important;
                                                                                `,
                                                                                css`
                                                                                    font-size: 18px !important;
                                                                                    font-weight: bold !important;
                                                                                    letter-spacing: 1.08px !important;
                                                                                    line-height: 25px !important;
                                                                                    margin-bottom: 15px !important;
                                                                                `,
                                                                                css`
                                                                                    font-size: 18px !important;
                                                                                    font-weight: bold !important;
                                                                                    letter-spacing: 1.08px !important;
                                                                                    line-height: 25px !important;
                                                                                    margin-bottom: 15px !important;
                                                                                `,
                                                                                css`
                                                                                    font-size: 18px !important;
                                                                                    font-weight: bold !important;
                                                                                    letter-spacing: 1.08px !important;
                                                                                    line-height: 25px !important;
                                                                                    margin-bottom: 15px !important;
                                                                                `,
                                                                            ],
                                                                            "min",
                                                                        )}
                                                                }
                                                            `}
                                                        >
                                                            {parse(slide.fields.heading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                    {slide.fields.subheading && (
                                                        <Typography
                                                            {...styles.subheadingText}
                                                            className="slide-subheading"
                                                            css={css`
                                                                p {
                                                                    color: #ffffff !important;
                                                                    font-size: 16px !important;
                                                                    font-weight: 200 !important;
                                                                    letter-spacing: 0 !important;
                                                                    line-height: 20px !important;
                                                                    margin-bottom: 0 !important;
                                                                    ${({ theme }: { theme: BaseTheme }) =>
                                                                        breakpointMediaQueries(
                                                                            theme,
                                                                            [
                                                                                css`
                                                                                    font-size: 16px !important;
                                                                                    font-weight: 200 !important;
                                                                                    letter-spacing: 0 !important;
                                                                                    line-height: 26px !important;
                                                                                `,
                                                                                css`
                                                                                    font-size: 16px !important;
                                                                                    font-weight: 200 !important;
                                                                                    letter-spacing: 0 !important;
                                                                                    line-height: 26px !important;
                                                                                `,
                                                                                css`
                                                                                    color: #ffffff !important;
                                                                                    font-size: 16px !important;
                                                                                    font-weight: 200 !important;
                                                                                    letter-spacing: 0 !important;
                                                                                    line-height: 20px !important;
                                                                                    margin-bottom: 0 !important;
                                                                                `,
                                                                                css`
                                                                                    color: #ffffff !important;
                                                                                    font-size: 16px !important;
                                                                                    font-weight: 200 !important;
                                                                                    letter-spacing: 0 !important;
                                                                                    line-height: 20px !important;
                                                                                    margin-bottom: 0 !important;
                                                                                `,
                                                                                css`
                                                                                    color: #ffffff !important;
                                                                                    font-size: 16px !important;
                                                                                    font-weight: 200 !important;
                                                                                    letter-spacing: 0 !important;
                                                                                    line-height: 20px !important;
                                                                                    margin-bottom: 0 !important;
                                                                                `,
                                                                            ],
                                                                            "min",
                                                                        )}
                                                                }
                                                            `}
                                                        >
                                                            {parse(slide.fields.subheading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </React.Fragment>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>

                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.nextArrowWrapper} className="slide-arrow-wrapper">
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            className="slide-arrow"
                            disabled={!canScrollNext}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: TimeLineCarousal,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.timeLineBackground,
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                tab: contentTab,
                displayName: "Timeline Background",
                defaultValue: "black",
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideYear } }) => slideYear || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                hideAdd: false,

                fieldDefinitions: [
                    {
                        name: "slideYear",
                        editorTemplate: "TextField",
                        displayName: "Date",
                        defaultValue: "",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },

                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },

                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "slidTextAlignment",
                        editorTemplate: "DropDownField",
                        defaultValue: "left",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,

                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });

                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
